// ── EmailTriagePage — external addresses we've corresponded with that aren't in the CRM ──
// This page is the feature's heartbeat. Capture links what it can match; every
// address it couldn't lands here, sorted by how much you actually email them.
// Working this list down is what makes the account timelines complete.
//
// Four outcomes per row: create the contact, link to one that already exists,
// ignore it once, or exclude it from capture forever.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { localFilter } from "../../../shared/format";
import { Btn, Card, ErrorMsg, Modal, Pagination, STh, SearchBar, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { useSortable, usePagination } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

const when = d => d ? new Date(d).toLocaleDateString("en-US", { year: "2-digit", month: "short", day: "numeric" }) : "—";

// "Jane Q. Public" -> { firstname: "Jane Q.", lastname: "Public" }. Best effort;
// the user sees and can fix it in the contact form before saving.
const splitName = (name, address) => {
  const n = (name || "").trim();
  if (!n || n.includes("@")) {
    const local = String(address || "").split("@")[0].replace(/[._-]+/g, " ").trim();
    const p = local.split(/\s+/);
    return { firstname: p[0] || "", lastname: p.slice(1).join(" ") || "" };
  }
  const parts = n.split(/\s+/);
  return parts.length === 1
    ? { firstname: parts[0], lastname: "" }
    : { firstname: parts.slice(0, -1).join(" "), lastname: parts[parts.length - 1] };
};

export const EmailTriagePage = () => {
  const { api, role, setModal, setConfirm: rawConfirm } = useApp();
  // Administrators and BDO managers see the whole queue and the firm-wide
  // actions; everyone else sees addresses on their own mail (the API scopes it).
  const seesAll = !!role.salesUnrestricted;

  // See MailCaptureAdminPage: the shared Confirm leaves closing to the caller.
  const setConfirm = (opts) => {
    if (!opts) return rawConfirm(null);
    rawConfirm({ ...opts, onConfirm: () => { rawConfirm(null); opts.onConfirm(); } });
  };
  const [rows, setRows] = useState([]);
  // How many rows exist server-side for this tab, which is not the same as how
  // many were returned. null = the API didn't tell us (older build).
  const [queueTotal, setQueueTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("new");
  const [traffic, setTraffic] = useState("all");  // Inbound only | Two-way | Outbound only | all
  const [busy, setBusy] = useState(null);          // address currently being acted on
  const [linkRow, setLinkRow] = useState(null);    // row whose Link dialog is open
  const [note, setNote] = useState(null);

  const load = () => {
    setLoading(true); setErr(null);
    api.get("getEmailActivity", { queue: status })
      .then(d => {
        // Newer API returns { rows, total, truncated }; older one a bare array.
        // Accept both so a half-deployed build degrades rather than blanks out.
        const list = Array.isArray(d) ? d : (d && Array.isArray(d.rows) ? d.rows : []);
        // The API supplies `domain`; derive it if we're talking to an older
        // build so the column and its sort still work.
        setRows(list.map(r => r.domain
          ? r
          : { ...r, domain: String(r.address || "").split("@")[1] || null }));
        setQueueTotal(Array.isArray(d) ? null : (d && typeof d.total === "number" ? d.total : null));
        setLoading(false);
      })
      .catch(e => {
        setErr(/Invalid object name/i.test(e.message) ? "Email capture is not set up yet." : e.message);
        setLoading(false);
      });
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const act = (body, msg) => {
    setBusy(body.address); setNote(null);
    api.post("resolveEmailAddress", body)
      .then(r => {
        setBusy(null); setLinkRow(null);
        setNote(msg + (r && r.linked ? ` (${r.linked} message${r.linked === 1 ? "" : "s"} linked)` : ""));
        load();
      })
      .catch(e => { setBusy(null); setErr(e.message); });
  };

  // Likely Account is set at capture time only, so a domain mapped to an
  // account later doesn't reach addresses already in the queue. This fills
  // those blanks in one pass (usp_BackfillLikelyAccount); it never overwrites.
  const fillGuesses = () => {
    setBusy("__fill__"); setNote(null);
    api.post("resolveEmailAddress", { action: "backfillguess" })
      .then(r => {
        setBusy(null);
        const n = (r && r.filled) || 0, amb = (r && r.ambiguous) || 0;
        setNote(`Likely Account filled on ${n} address${n === 1 ? "" : "es"}`
          + (amb ? ` · ${amb} skipped (domain mapped to more than one account)` : ""));
        load();
      })
      .catch(e => { setBusy(null); setErr(e.message); });
  };

  // Creating the contact goes through the normal contact form — one code path,
  // one set of validation. On save we just re-run matching for the address and
  // the backend links its whole history by email.
  const createContact = (r) => {
    const { firstname, lastname } = splitName(r.displayname, r.address);
    const domain = String(r.address || "").split("@")[1] || "";
    setModal({
      type: "contact-add",
      accountid: r.guessaccountid || null,
      // companyname rides along so the account stays named in the picker even
      // before the account list finishes loading.
      initial: {
        accountid: r.guessaccountid || "",
        companyname: r.guesscompanyname || "",
        email: r.address, firstname, lastname,
      },
      hint: r.guessaccountid
        ? `Pre-filled from the ${domain} domain mapping. Change it if this person belongs elsewhere.`
        : `No account is mapped to ${domain} — pick the right one so this contact isn't orphaned.`,
      onDone: () => act({ action: "rematch", address: r.address }, "Contact created and history linked"),
    });
  };

  // The other kind of person who emails us. A broker, an accountant, a banker
  // who sends deals — someone to build a relationship with rather than someone
  // who belongs to a borrower. Filing them as a contact would have needed an
  // account they have nothing to do with.
  //
  // Same shape as createContact: the normal form, then a re-match that links
  // their whole captured history by address.
  const createReferralSource = (r) => {
    const { firstname, lastname } = splitName(r.displayname, r.address);
    const domain = String(r.address || "").split("@")[1] || "";
    setModal({
      type: "referralsource-add",
      prefill: {
        email: r.address, firstname, lastname,
        // The domain is a reasonable first guess at the firm and a poor final
        // answer, so it is offered rather than assumed — an empty company on a
        // referral source is worse than one that needs correcting, because the
        // list is sorted and searched by it.
        company_name: domain ? domain.replace(/\.(com|net|org|co\.uk|io)$/i, "") : "",
      },
      hint: `Company name guessed from ${domain || "the address"} — correct it before saving. `
          + `A referral source has no account: they introduce business rather than borrow it.`,
      onDone: () => act({ action: "rematch", address: r.address },
                        "Referral source created and history linked"),
    });
  };

  // Traffic: inbound only (nobody here ever wrote to them — the usual shape of
  // spam and cold pitches) vs two-way. trafficrank sorts inbound-only first.
  const TRAFFIC_RANK = { "Inbound only": 0, "Outbound only": 1, "Two-way": 2 };
  const withTraffic = rows.map(r => ({ ...r, trafficrank: r.traffic ? TRAFFIC_RANK[r.traffic] : null }));
  const filtered = localFilter(withTraffic, q, ["address", "domain", "displayname", "guesscompanyname", "topinternalname", "traffic"])
    .filter(r => traffic === "all" || r.traffic === traffic);
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, "messagecount", "desc");
  const { paginated: displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 50);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const StatusBtn = ({ id, label }) => (
    <Btn key={id} variant={status === id ? "primary" : "default"} onClick={() => { setStatus(id); reset(); }}>{label}</Btn>
  );

  // The badge shows the whole queue for this tab. When a search is narrowing it,
  // or the server capped what it sent, say so rather than showing one number
  // that could mean any of three things.
  const serverTotal = queueTotal === null ? rows.length : queueTotal;
  const capped = rows.length < serverTotal;
  const countBadge = q
    ? `${total} of ${serverTotal}`
    : capped ? `${rows.length} of ${serverTotal}` : String(serverTotal);

  return <>
    <SectionHeader count={countBadge} actions={[
      <SearchBar key="s" value={q} onChange={v => { setQ(v); reset(); }} placeholder="Search address, domain, name..." />,
      <StatusBtn key="new" id="new" label="Needs Review" />,
      <StatusBtn key="resolved" id="resolved" label="Resolved" />,
      <StatusBtn key="ignored" id="ignored" label="Ignored" />,
      <select key="tr" value={traffic} onChange={e => { setTraffic(e.target.value); reset(); }}
              title="Inbound only: nobody here has ever written to this address"
              style={{ fontSize: 13, padding: "5px 8px", borderRadius: 6, border: "0.5px solid #c8cdd2", background: "#fff" }}>
        <option value="all">All traffic</option>
        <option value="Inbound only">Inbound only</option>
        <option value="Two-way">Two-way</option>
        <option value="Outbound only">Outbound only</option>
      </select>,
      seesAll && <Btn key="fg" disabled={busy === "__fill__"} onClick={fillGuesses}>{busy === "__fill__" ? "Filling..." : "Fill Likely Accounts"}</Btn>,
      seesAll && <Btn key="rm" onClick={() => act({ action: "rematch" }, "Re-matched all captured email")}>Re-match All</Btn>,
    ].filter(Boolean)} />

    {!seesAll && <div style={{ fontSize: 12, background: "#E6F7FC", border: "0.5px solid " + B.cyanLight, borderRadius: 8,
                                padding: "8px 12px", marginBottom: 10, color: B.cyanDark }}>
      Showing addresses from email <strong>you</strong> sent or received. Map them to a contact, lead or referral
      source and that correspondence shows up on the record's timeline.
    </div>}

    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
      External addresses that emailed the team but match no contact, lead or referral source.
      <strong>+ Referral Source</strong> is for someone who sends us business — a broker, an
      accountant, a banker — rather than someone who belongs to a borrower.
      <strong>+ Contact</strong> creates
      a new person; <strong>Link to person</strong> attaches the address to someone already in the CRM. Either way
      the address attaches to a <em>person</em>, and the account comes from that person's record — so every message
      from this address, going back through everything already captured, lands on both their timeline and their
      account's.
    </div>

    {capped && (
      <div style={{ fontSize: 12, color: "#7a5200", background: "#FAEEDA", border: "0.5px solid #EBD3A0",
                    borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
        Showing the {rows.length} busiest of {serverTotal} addresses. Resolve or exclude some and the rest
        will appear — or clear the noise in one pass from Admin → Integrations → Microsoft 365 Mail →
        Capture Rules.
      </div>
    )}

    {note && (
      <div style={{ fontSize: 13, color: "#0F6E56", background: "#E1F5EE", border: "0.5px solid #B9E3D4",
                    borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>{note}</div>
    )}

    <Card style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          <STh label="Address"      sortKey="address"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Domain"       sortKey="domain"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Name"         sortKey="displayname"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Likely Account" sortKey="guesscompanyname" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          {/* Sorted on the NAME, not the count: grouping an owner's addresses
              together is what makes this column worth having — you work one
              person's queue in a sitting rather than hopping between them. */}
          <STh label="Mostly With"  sortKey="topinternalname"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Messages"     sortKey="messagecount"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Traffic"      sortKey="trafficrank"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="First Seen"   sortKey="firstseenutc"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Last Seen"    sortKey="lastseenutc"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <Th></Th>
        </tr></thead>
        <tbody>{displayed.map(r => (
          <tr key={r.id}>
            <Td style={{ fontWeight: 500 }}>{r.address}</Td>
            {/* Click a domain to narrow the page to it — the fastest way to see
                how much of the queue one company or one noise sender accounts
                for before deciding to map it or exclude it. */}
            <Td>{r.domain
              ? <span onClick={() => { setQ(r.domain); reset(); }}
                      title={"Show only " + r.domain}
                      style={{ cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}>
                  {r.domain}
                </span>
              : "—"}</Td>
            <Td>{r.displayname || "—"}</Td>
            {/* ORDER MATTERS AND IS NOT OBVIOUS. These cells carry no labels of
                their own, so the only thing tying a value to a column is its
                position in this row matching its position in the <thead> above:
                Likely Account, then Mostly With. They were the other way round
                for one build, which put "unknown domain" under Mostly With and
                a dash under Likely Account — two columns that each looked
                individually broken rather than one that looked swapped. If you
                add a column here, add it in both places in the same place. */}
            <Td>{r.guesscompanyname
              ? <span>{r.guesscompanyname}</span>
              : <span style={{ color: "var(--color-text-secondary)" }}>unknown domain</span>}</Td>
            <Td>{r.topinternalname
              ? <span title={[r.topinternaladdress,
                              r.topinternalcount
                                ? `on ${r.topinternalcount} of ${r.messagecount} message${r.messagecount === 1 ? "" : "s"}`
                                : null].filter(Boolean).join(" · ")}>
                  {r.topinternalname}
                  {/* The share, only when it is not everything. "4" beside a
                      name on a 4-message address is noise; it earns its place
                      when one of us is on some of the thread and not the rest. */}
                  {r.topinternalcount && r.messagecount && r.topinternalcount < r.messagecount ? (
                    <span style={{ color: "var(--color-text-secondary)", marginLeft: 6 }}>
                      {r.topinternalcount}/{r.messagecount}
                    </span>
                  ) : null}
                </span>
              : <span style={{ color: "var(--color-text-secondary)" }}>—</span>}</Td>
            <Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.messagecount}</Td>
            <Td>{r.traffic ? <span title={`${r.inboundcount || 0} received from them · ${r.outboundcount || 0} sent to them`}
                   style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                            background: r.traffic === "Two-way" ? "#E1F5EE" : r.traffic === "Inbound only" ? "#FDF0D5" : "#E6F1FB",
                            color: r.traffic === "Two-way" ? "#0F6E56" : r.traffic === "Inbound only" ? "#7A5200" : "#185FA5" }}>
                  {r.traffic}</span> : "—"}
              {r.traffic && <span style={{ fontSize: 11, color: "var(--color-text-secondary)", marginLeft: 6, fontVariantNumeric: "tabular-nums" }}>
                {r.inboundcount || 0} in · {r.outboundcount || 0} out</span>}
            </Td>
            <Td>{when(r.firstseenutc)}</Td>
            <Td>{when(r.lastseenutc)}</Td>
            <Td>
              {status !== "new" ? <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                {r.status === "resolved" ? "Resolved"
                  : r.resolvedtype === "Excluded" ? "Excluded" : "Ignored"}
              </span> : role.canAccessSales && (
                <div style={{ display: "flex", gap: 6 }}>
                    <Btn variant="primary" onClick={() => createContact(r)}>+ Contact</Btn>
                    {/* Amber, not a plain default: this is the second CREATE
                        action on the row and the choice between it and
                        + Contact is the actual decision being made here.
                        Rendering it as a quiet outline would make it read as
                        secondary when it is an equal alternative. */}
                    <Btn variant="amber" onClick={() => createReferralSource(r)}
                         title="Someone who sends us business rather than someone at a borrower">
                      + Referral Source</Btn>
                    <Btn onClick={() => setLinkRow(r)} title="Attach this address to a person already in the CRM">Link to person</Btn>
                    <Btn disabled={busy === r.address}
                      onClick={() => act({ action: "ignore", address: r.address }, "Ignored " + r.address)}>Ignore</Btn>
                    {seesAll && <Btn variant="danger" disabled={busy === r.address}
                      onClick={() => setConfirm({
                        title: "Exclude address from capture",
                        confirmLabel: "Exclude",
                        msg: "Stop capturing email from " + r.address + "? Mail already captured is kept.",
                        onConfirm: () => act({ action: "exclude", address: r.address }, "Excluded " + r.address),
                      })}>Exclude</Btn>}
                </div>
              )}
            </Td>
          </tr>
        ))}</tbody>
        {displayed.length === 0 && (
          <tbody><tr><Td colSpan={9} style={{ color: "var(--color-text-secondary)" }}>
            {status === "new" ? "Nothing to review — every captured address is matched." : "Nothing here."}
          </Td></tr></tbody>
        )}
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={9} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid " + B.cyan }}>
            Total ({sorted.length})
          </td>
        </tr></tfoot>
      </table>
      <Pagination page={page} totalPages={totalPages} total={total} pageSize={50} onPage={setPage} />
    </Card>

    {linkRow && (
      <LinkPersonDialog
        row={linkRow}
        api={api}
        busy={busy === linkRow.address}
        onCancel={() => setLinkRow(null)}
        onLink={(entityid, entitytype) =>
          act({ action: "link", address: linkRow.address, entitytype, entityid }, "Linked " + linkRow.address)}
      />
    )}
  </>;
};

// ── LinkPersonDialog ─────────────────────────────────────────────────────────
// Attaching an address to an existing person. This lived inline in the table
// row, which was a mistake twice over: the row is off the right-hand edge on a
// normal screen, so you had to scroll sideways to reach it and sideways again
// to cancel; and widening the list to every contact in the database to fix an
// empty-dropdown case made the common case worse.
//
// So: a dialog, and the account scope is back as the DEFAULT. When the domain
// matched an account you get that account's handful of people immediately —
// which is the fast path and almost always the right answer. "Search all
// people" is one click away for when they're not there, or when nothing
// matched the domain at all (in which case it opens on all people, because
// scoping to nothing is what made this useless before).
const LinkPersonDialog = ({ row, api, busy, onCancel, onLink }) => {
  const hasGuess = !!row.guessaccountid;
  const [scope, setScope] = useState(hasGuess ? "account" : "all");
  const [byAccount, setByAccount] = useState(null);   // null = not fetched yet
  const [allPeople, setAllPeople] = useState(null);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState("");
  const [pick, setPick] = useState("");

  // Each list is fetched once and kept. The account list is small and fast;
  // the full list is only ever pulled if you ask for it.
  useEffect(() => {
    let alive = true;
    const wantAccount = scope === "account" && hasGuess;
    if (wantAccount ? byAccount !== null : allPeople !== null) return;
    setLoading(true);
    api.get("getContacts", wantAccount ? { accountid: row.guessaccountid } : undefined)
      .then(d => {
        if (!alive) return;
        const list = Array.isArray(d) ? d : [];
        if (wantAccount) setByAccount(list); else setAllPeople(list);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        if (wantAccount) setByAccount([]); else setAllPeople([]);
        setLoading(false);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const source = (scope === "account" && hasGuess ? byAccount : allPeople) || [];
  const people = localFilter(source, term, ["firstname", "lastname", "email", "companyname"]).slice(0, 300);
  const truncated = source.length > people.length && people.length === 300;

  const nameOf = c => [c.firstname, c.lastname].filter(Boolean).join(" ") || c.email || (c.Id || c.id);
  const idOf = c => String(c.Id || c.id);

  return (
    <Modal title={"Link " + row.address} onClose={onCancel}>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
        Attaches this address to a person. Every message from it — including everything already
        captured — lands on that person's timeline and their account's.
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        {hasGuess && (
          <>
            <Btn variant={scope === "account" ? "primary" : "default"}
              onClick={() => { setScope("account"); setPick(""); }}>
              At {row.guesscompanyname || "the matched account"}
            </Btn>
            <Btn variant={scope === "all" ? "primary" : "default"}
              onClick={() => { setScope("all"); setPick(""); }}>Search all people</Btn>
          </>
        )}
        <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Filter by name, email, company..."
          style={{ flex: 1, minWidth: 180, padding: "6px 10px", fontSize: 13, borderRadius: 4, border: "1px solid #999" }} />
      </div>

      <div style={{ border: "1px solid var(--color-border-secondary)", borderRadius: 6,
                    maxHeight: 320, overflowY: "auto", marginBottom: 12 }}>
        {loading && <div style={{ padding: "14px 12px", fontSize: 13, color: "var(--color-text-secondary)" }}>Loading…</div>}

        {!loading && people.length === 0 && (
          <div style={{ padding: "14px 12px", fontSize: 13, color: "var(--color-text-secondary)" }}>
            {scope === "account"
              ? <>Nobody at {row.guesscompanyname || "this account"} matches. Try <strong>Search all people</strong>, or close this and press <strong>+ Contact</strong>.</>
              : <>No match. Close this and press <strong>+ Contact</strong> to create the person.</>}
          </div>
        )}

        {!loading && people.map(c => {
          const id = idOf(c);
          const on = pick === id;
          return (
            <div key={id} onClick={() => setPick(id)}
              style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer", display: "flex", gap: 10,
                       alignItems: "baseline", justifyContent: "space-between",
                       background: on ? B.cyanLight : "transparent",
                       borderLeft: "3px solid " + (on ? B.cyan : "transparent") }}>
              <span style={{ fontWeight: on ? 600 : 400 }}>{nameOf(c)}</span>
              <span style={{ fontSize: 11, color: "var(--color-text-secondary)", textAlign: "right" }}>
                {c.email || "no email"}{scope === "all" && c.companyname ? " · " + c.companyname : ""}
              </span>
            </div>
          );
        })}

        {truncated && (
          <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--color-text-secondary)" }}>
            Showing the first 300 — narrow it with the filter.
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onCancel}>Cancel</Btn>
        <Btn variant="primary" disabled={!pick || busy} onClick={() => onLink(pick, "Contact")}>
          {busy ? "Linking…" : "Link"}
        </Btn>
      </div>
    </Modal>
  );
};
